import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FilePlus, FolderInput, Link2, MoreHorizontal, Trash } from 'lucide-react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import { toast } from 'sonner'
import { canCreateSubPage, getPageDepth } from '../utils/pageTreeUtils'
import { docRoute } from '../utils/docRoute'
import { PageDescriptionEditor } from './PageDescriptionEditor'
import { ExtractTasksDialog } from './ExtractTasksDialog'
import { DocOutline } from './DocOutline'
import { DocSubpagesFooter } from './DocSubpagesFooter'
import { DocStarter } from './DocStarter'
import { DocLevelChip } from './DocLevelChip'
import { MoveDocPopover } from './MoveDocPopover'
import type { DocLocationPatch } from '../utils/docMove'
import type { DependencyList, ReactNode } from 'react'
import type { Editor } from '@tiptap/core'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { EditorActions } from '@/components/editor/editorActions'
import { PhoneFormatBar } from '@/components/editor/PhoneFormatBar'
import { AiSparkleButton } from '@/components/app/ai/AiSparkle'
import { useBacklinks, useDocumentContributors, useEntityLinks, usePageMutations, useProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { usePageLayout } from '@/hooks/usePageLayout'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { DocumentHeader } from '@/components/app/DocumentHeader'
import { useCoverImage } from '@/components/features/cover-image'
import { ExplicitLinksSection } from '@/components/features/backlinks/ExplicitLinksSection'
import { BacklinksSection } from '@/components/features/backlinks/BacklinksSection'
import { CommentSidebar } from '@/components/editor/CommentSidebar'
import { useAiAvailable } from '@/lib/ai/useAiAvailable'
import { ENTITY_TYPES } from '@/lib/constants/entityTypes'
import { LINK_TYPES } from '@/lib/db/rxdb/schemas/entityLink'
import { useEntityLinkSuggestions } from '@/components/editor/useEntityLinkSuggestions'
import { useEditorTaskCreator } from '@/hooks/useEditorTaskCreator'
import { useEntityLinkNavigation } from '@/hooks/useEntityLinkNavigation'
import { logger } from '@/lib/monitoring/logger'
import { LogCategory } from '@/lib/monitoring/types'

export interface PageDetailsViewProps {
  page: PageDocument
  project: ProjectDocument
  pages: Array<PageDocument>
  workspaceId: string
  userId: string | null
  pageSlug: string
  sidebar: ReactNode
  breadcrumb: ReactNode
  layoutDeps: DependencyList
  onNavigateToSubPage: (slug: string) => void
  onNavigateAfterDelete: (parentPage: PageDocument | null) => void
}

export function PageDetailsView({
  page,
  project,
  pages,
  workspaceId,
  userId,
  pageSlug,
  sidebar,
  breadcrumb,
  layoutDeps,
  onNavigateToSubPage,
  onNavigateAfterDelete,
}: PageDetailsViewProps) {
  const [titleValue, setTitleValue] = useState(page.title)
  const { contributorUserIds } = useDocumentContributors(page.id)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showExtractDialog, setShowExtractDialog] = useState(false)
  const aiAvailable = useAiAvailable()
  const [showComments, setShowComments] = useState(false)
  const [activeAnchorId, setActiveAnchorId] = useState<string | null>(null)
  const [editor, setEditor] = useState<Editor | null>(null)

  const { createPage, updatePage, deletePage } = usePageMutations()
  const { t } = useTranslation('projects')
  const navigate = useNavigate()
  const workspaceSlug = useLoginStore((s) => s.workspaceSlug)
  const { projects } = useProjects()
  const [isMoveOpen, setIsMoveOpen] = useState(false)

  // After a move, follow the doc to its new URL (space doc vs project doc).
  const handleMovedRef = useRef<(location: DocLocationPatch) => void>(() => {})
  handleMovedRef.current = (location) => {
    if (!workspaceSlug) return
    const route = docRoute(workspaceSlug, { projectId: location.projectId, slug: page.slug }, projects)
    if (route) void navigate(route)
  }

  useEntityLinkSuggestions()

  const handleEntityLinkClick = useEntityLinkNavigation()

  const handleAddComment = useCallback((anchorId: string) => {
    setActiveAnchorId(anchorId)
    setShowComments(true)
  }, [])

  const handleCommentClick = useCallback((anchorId: string) => {
    setActiveAnchorId(anchorId)
    setShowComments(true)
  }, [])

  const handleCloseComments = useCallback(() => {
    setShowComments(false)
    setActiveAnchorId(null)
  }, [])

  const handleCancelNewComment = useCallback(() => {
    if (activeAnchorId && editor) {
      editor.commands.removeCommentHighlight(activeAnchorId)
    }
    setActiveAnchorId(null)
  }, [activeAnchorId, editor])

  const handleEditorReady = useCallback((next: Editor | null) => {
    setEditor(next)
  }, [])

  // With no links, backlinks or family, the footer is one quiet line (design 32a).
  const { links } = useEntityLinks(page.id)
  const { backlinks } = useBacklinks(page.id)
  const [linksOpened, setLinksOpened] = useState(false)
  useEffect(() => setLinksOpened(false), [page.id])
  const hasLinks = linksOpened || links.some((l) => l.linkType === LINK_TYPES.EXPLICIT) || backlinks.length > 0
  const hasFamily = useMemo(() => {
    const live = pages.filter((p) => !p.isDeleted)
    if (page.parentPageId && live.some((p) => p.id === page.parentPageId)) return true
    if (live.some((p) => p.parentPageId === page.id)) return true
    // A later sibling shows as "Next" in the subpages footer.
    const siblings = live
      .filter((p) => p.projectId === page.projectId && (p.parentPageId ?? null) === (page.parentPageId ?? null))
      .sort((a, b) => (a.sortOrder ?? '').localeCompare(b.sortOrder ?? '') || a.createdOn - b.createdOn)
    const index = siblings.findIndex((p) => p.id === page.id)
    return index >= 0 && index < siblings.length - 1
  }, [pages, page.id, page.parentPageId, page.projectId])

  const pageDepth = useMemo(() => getPageDepth(pages, page.id), [pages, page.id])
  const canAddSubPage = canCreateSubPage(page, pageDepth)

  // Editor actions (design 7a/7b): tasks land in this doc's project; /page
  // nests a new doc here without leaving the page.
  const createTaskFromEditor = useEditorTaskCreator(project.id)
  const editorActions = useMemo<EditorActions>(
    () => ({
      createTask: createTaskFromEditor,
      createSubPage: canAddSubPage
        ? async (title: string) => {
            if (!workspaceId || !userId) return null
            try {
              const created = await createPage({
                projectId: project.id,
                parentPageId: page.id,
                title,
                slug: crypto.randomUUID().slice(0, 8),
                icon: null,
                iconType: null,
                sortOrder: null,
              })
              return { id: created.id, label: created.title || title }
            } catch (error) {
              logger.logError(error as Error, LogCategory.DATABASE, { operation: 'createSubPage', source: 'slash' })
              toast.error('Unable to create sub-page')
              return null
            }
          }
        : undefined,
    }),
    [createTaskFromEditor, canAddSubPage, workspaceId, userId, createPage, project.id, page.id],
  )

  const handleCreateSubPage = useCallback(async () => {
    if (!workspaceId || !userId) return
    const newSlug = crypto.randomUUID().slice(0, 8)
    try {
      await createPage({
        projectId: project.id,
        parentPageId: page.id,
        title: '',
        slug: newSlug,
        icon: null,
        iconType: null,
        sortOrder: null,
      })
      onNavigateToSubPage(newSlug)
    } catch (error) {
      logger.logError(error as Error, LogCategory.DATABASE, { operation: 'createSubPage', source: 'button' })
      toast.error('Unable to create sub-page')
    }
  }, [project.id, page.id, workspaceId, userId, createPage, onNavigateToSubPage])

  const handleDeletePage = useCallback(async () => {
    setIsDeleting(true)
    try {
      await deletePage(page.id)
      toast.success('Page deleted')
      const parent = page.parentPageId
        ? pages.find((p) => p.id === page.parentPageId && !p.isDeleted) ?? null
        : null
      onNavigateAfterDelete(parent)
    } catch {
      toast.error('Unable to delete page')
    } finally {
      setIsDeleting(false)
    }
  }, [page.id, page.parentPageId, pages, deletePage, onNavigateAfterDelete])

  // The breadcrumb actions are pushed into layout context and only re-pushed
  // when the layout deps change, so the buttons must reach the handlers
  // through a ref — otherwise they keep the first render's mutations, whose
  // RxDB handle is still null ("Database not initialized").
  const headerHandlersRef = useRef({ createSubPage: handleCreateSubPage, deletePage: handleDeletePage })
  headerHandlersRef.current = { createSubPage: handleCreateSubPage, deletePage: handleDeletePage }

  const breadcrumbActions = useMemo(() => (
    <>
      {aiAvailable && (
        <AiSparkleButton
          tone="subtle"
          aria-label="Extract tasks from this page (AI)"
          title="Extract tasks from this page (AI)"
          className="size-7"
          iconClassName="size-3"
          onClick={() => setShowExtractDialog(true)}
        />
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Add sub-page"
        className="text-muted-foreground hover:text-foreground"
        disabled={!canAddSubPage}
        onClick={() => { void headerHandlersRef.current.createSubPage() }}
      >
        <FilePlus className="size-3.5" />
      </Button>
      <MoveDocPopover
        page={page}
        open={isMoveOpen}
        onOpenChange={setIsMoveOpen}
        onMoved={(location) => handleMovedRef.current(location)}
      >
        <span className="inline-flex">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={t('docs.options')}
                className="text-muted-foreground hover:text-foreground"
              >
                <MoreHorizontal className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44" onCloseAutoFocus={(e) => e.preventDefault()}>
              <DropdownMenuItem onSelect={() => { setTimeout(() => setIsMoveOpen(true), 0) }}>
                <FolderInput className="size-4" />
                {t('docs.move.menuItem')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </MoveDocPopover>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Delete page"
            className="text-muted-foreground hover:text-destructive"
          >
            <Trash className="size-3.5" />
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete &quot;{page.title || 'this page'}&quot;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete
              this page and all its sub-pages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={isDeleting}
              onClick={async () => { await headerHandlersRef.current.deletePage() }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  ), [page, canAddSubPage, isDeleting, aiAvailable, isMoveOpen, t])

  usePageLayout(
    { sidebarContent: sidebar, breadcrumb, breadcrumbActions },
    [...layoutDeps, isDeleting, canAddSubPage, aiAvailable, page.title, page.id, page.projectId, page.parentPageId, isMoveOpen],
  )

  const {
    displayUrl: coverDisplayUrl,
    meta: coverMeta,
    hasCover,
    isUploading: isCoverUploading,
    uploadProgress: coverUploadProgress,
    setCoverFromUnsplash,
    uploadCustomImage,
    removeCoverImage,
    updatePosition: updateCoverPosition,
  } = useCoverImage({
    entityId: page.id,
    workspaceId,
    coverImageUrl: page.coverImageUrl ?? null,
    coverImageType: page.coverImageType ?? null,
    coverImageMeta: page.coverImageMeta ?? null,
    updateEntity: updatePage,
    uploadCategory: 'page-cover',
  })

  useEffect(() => {
    setTitleValue(page.title)
  }, [page.title, page.slug])

  const handleTitleBlur = useCallback(async () => {
    const trimmed = titleValue.trim()
    const currentTitle = page.title.trim()
    if (trimmed.length === 0) { setTitleValue(currentTitle); return }
    if (trimmed === currentTitle) { if (titleValue !== currentTitle) setTitleValue(currentTitle); return }
    try {
      await updatePage(page.id, { title: trimmed })
      setTitleValue(trimmed)
    } catch {
      toast.error('Unable to update page title')
      setTitleValue(currentTitle)
    }
  }, [page.id, page.title, titleValue, updatePage])

  const handleIconUpdate = useCallback(
    async (value: { icon: string | null; iconType: string | null; iconColor?: string | null } | null) => {
      try {
        await updatePage(page.id, {
          icon: value?.icon ?? null,
          iconType: value?.iconType ?? null,
          iconColor: value?.iconColor ?? null,
        })
      } catch {
        toast.error('Unable to update page icon')
      }
    },
    [page.id, updatePage],
  )

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={pageSlug}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="relative flex min-h-full flex-1 overflow-y-auto"
      >
        {/* The doc is a reading-width paper sheet with its outline beside it (design 3b). */}
        <div className="flex min-w-0 flex-1 justify-center gap-8 px-2 py-3 md:px-6 md:py-6">
        <div className="min-w-0 max-w-[860px] flex-1 self-start rounded-xl border bg-card pb-6 shadow-xs">
          <DocumentHeader
            coverDisplayUrl={coverDisplayUrl}
            coverMeta={coverMeta}
            coverImageType={page.coverImageType}
            hasCover={hasCover}
            isCoverUploading={isCoverUploading}
            coverUploadProgress={coverUploadProgress}
            onCoverFromUnsplash={(photo) => void setCoverFromUnsplash(photo)}
            onCoverUpload={uploadCustomImage}
            onCoverRemove={() => void removeCoverImage()}
            onCoverPositionChange={(y) => void updateCoverPosition(y)}
            icon={page.icon}
            iconType={page.iconType}
            iconColor={page.iconColor}
            onIconChange={(value) => { void handleIconUpdate(value) }}
            title={titleValue}
            titlePlaceholder={t('docs.titlePlaceholder')}
            autoFocusTitle={!page.title}
            onTitleEnter={() => editor?.commands.focus('start')}
            onTitleChange={setTitleValue}
            onTitleBlur={() => { void handleTitleBlur() }}
            maxTitleLength={500}
            contributorUserIds={contributorUserIds}
          >
            <div className="mt-3">
              <DocLevelChip page={page} location={project} />
            </div>
            <div className="prose-container mt-4 pb-4">
              <PageDescriptionEditor
                page={page}
                canEdit
                onAddComment={handleAddComment}
                onCommentClick={handleCommentClick}
                onEditorReady={handleEditorReady}
                actions={editorActions}
              />
            </div>

            <div className="prose-container">
              <DocStarter editor={editor} />
            </div>
            <PhoneFormatBar editor={editor} />

            {hasLinks || hasFamily ? (
              <div className="prose-container mt-6 space-y-4 pb-12">
                {hasLinks ? (
                  <ExplicitLinksSection
                    entityId={page.id}
                    entityType={ENTITY_TYPES.DOCUMENT}
                    entityName={page.title || ''}
                    workspaceId={workspaceId}
                    onLinkClick={handleEntityLinkClick}
                    defaultAdding={linksOpened}
                  />
                ) : (
                  <NoLinksButton onClick={() => setLinksOpened(true)} />
                )}
                <BacklinksSection
                  entityId={page.id}
                  onLinkClick={handleEntityLinkClick}
                />
                <DocSubpagesFooter
                  page={page}
                  pages={pages}
                  canAddSubPage={canAddSubPage}
                  onAddSubPage={() => void handleCreateSubPage()}
                  onOpenPage={onNavigateToSubPage}
                />
              </div>
            ) : (
              <div className="prose-container pb-10">
                <div className="mt-7 flex items-center gap-4 border-t pt-3 text-[12.5px] text-muted-foreground" data-testid="doc-footer-compact">
                  <NoLinksButton onClick={() => setLinksOpened(true)} />
                  {canAddSubPage && (
                    <button
                      type="button"
                      onClick={() => void handleCreateSubPage()}
                      className="flex items-center gap-1.5 hover:text-foreground"
                    >
                      <FilePlus className="size-3.5" />
                      {t('docs.addSubpage')}
                    </button>
                  )}
                </div>
              </div>
            )}
          </DocumentHeader>
        </div>
        <aside className="sticky top-6 hidden w-52 shrink-0 self-start pt-24 xl:block">
          <DocOutline editor={editor} />
        </aside>
        </div>
        {showComments ? (
          <CommentSidebar
            pageId={page.id}
            workspaceId={workspaceId}
            editor={editor}
            activeAnchorId={activeAnchorId}
            onClose={handleCloseComments}
            onCancelNewComment={handleCancelNewComment}
          />
        ) : null}
      </motion.div>
      <ExtractTasksDialog
        open={showExtractDialog}
        onOpenChange={setShowExtractDialog}
        documentId={page.id}
        pageTitle={page.title || undefined}
      />
    </AnimatePresence>
  )
}

/** "No links": opens the link picker in place of the empty Links section. */
function NoLinksButton({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation('projects')
  return (
    <button type="button" onClick={onClick} className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground hover:text-foreground">
      <Link2 className="size-3.5" />
      {t('docs.noLinks')}
    </button>
  )
}
